import { vec2 } from 'gl-matrix';
import { gl } from '../../../Engine';
import { DoseDistribution } from '../../../data/dose/DoseDistribution';
import { TextureView } from '../../../view/TextureView';
import { PlaneView } from '../../../view/views/PlaneView';
import { ShaderProgram } from '../../utils/ShaderProgram';
import { UniformVec2 } from '../../utils/uniforms/UniformVec2';
import { Renderer } from '../Renderer';
import { UniformFloat } from '../../utils/uniforms/UniformFloat';

/**
 * {@link DoseRenderer} is used to render {@link DoseDistribution DoseDistributions}.\
 * This {@link render renders} the {@link DoseDistribution}'s texture\
 * so that each voxel gets a color value based on the dose distribution value at that location.
 */
export class DoseRenderer implements Renderer {
    /**
     * The {@link DefaultDoseShader} that {@link render renders} the {@link DoseDistribution}.\
     * The dose is rendered so that each voxel's color is determined by it's dose distribution value.
     */
    private defaultShader: DefaultDoseShader;

    /**
     * Constructor that constructs the {@link defaultShader}.
     */
    public constructor() {
        this.defaultShader = new DefaultDoseShader();
    }

    /**
     * Method to render an {@link Array} of {@link DoseDistribution DoseDistributions} on a {@link TextureView}.\
     * This uses the {@link DefaultDoseShader}, which renders the {@link DoseTexture} colors using linear HSV algorithm.\
     * Values close to 0 get blue values that go through the color spectrum to red when values are close to {@link DoseDistribution.range value range's} maximum value.\
     * Voxels whose intensity is less than 10% of the maximum value are discarded.
     * @throws if {@link view} is not an instance of one of the {@link TextureView types} supported by the {@link DoseRenderer}.
     * @param view The {@link TextureView} defining the {@link TextureView.projectionViewMatrix projectionSpace} that the {@link DoseDistribution DoseDistributions} should be rendered.
     * @param doses {@link Array} of {@link DoseDistribution DoseDistributions} within the {@link Environment} and should be rendered on the {@link view}.
     * @returns True if the DoseDistributions were succesfully rendered, even if there was nothing to render.\
     * False if an exception occured preventing the render.
     */
    public render(view: TextureView, doses: DoseDistribution[]): boolean {
        if (view instanceof PlaneView) {
            this.defaultShader.start();
            for (const dose of doses) {
                if (!dose.isVisible) {
                    continue;
                }
                const vao = dose.crossSection(view.projectionViewMatrix);
                if (vao) {
                    const selectedRangeMin =
                        dose.range[0] +
                        (dose.range[1] - dose.range[0]) *
                            dose.selectedDoseRange[0];
                    const selectedRangeMax =
                        dose.range[0] +
                        (dose.range[1] - dose.range[0]) *
                            dose.selectedDoseRange[1];
                    const selectedRange = vec2.fromValues(
                        selectedRangeMin,
                        selectedRangeMax
                    );
                    const alphaLevelOpacity = dose.alphaLevelOpacity;
                    dose.bindTexture();
                    this.defaultShader.range.loadVec2(dose.range);
                    this.defaultShader.selectedRange.loadVec2(selectedRange);
                    this.defaultShader.alphaLevelOpacity.loadFloat(
                        alphaLevelOpacity
                    );

                    vao.bind(0, 1);
                    gl.drawElements(
                        gl.TRIANGLES,
                        vao.indexCount,
                        gl.UNSIGNED_INT,
                        0
                    );
                    vao.unbind(0, 1);
                    vao.delete();
                }
            }
            this.defaultShader.stop();
            return true;
        } else {
            throw new Error(
                `DoseRendering on View of type ${view.constructor} currently not supported.`
            );
        }
    }

    /**
     * Method to delete the {@link DoseRenderer}, which will {@link DefaultDoseShader.delete delete} the unerlaying {@link defaultShader}\
     * deallocating the WebGL memory consumed by this object.
     */
    delete(): void {
        this.defaultShader.delete();
    }
}

/**
 * {@link ShaderProgram} That renders the {@link DoseTexture} colors using linear HSV algorithm.\
 * Values close to 0 get blue values that go through the color spectrum to red when values are close to {@link range range's} y-value.\
 * Voxels whose intensity is less than 10% of the maximum value are discarded.
 */
class DefaultDoseShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es
    
    precision highp float;
    
    in vec2 position;
    in vec3 textureCoords;
    
    out vec3 pass_textureCoordinates;

    void main(){
        
        gl_Position = vec4(position, 0.0, 1.0);
        
        pass_textureCoordinates = textureCoords;
    }
    `;

    private static readonly FRAGMENT_CONTENT = `#version 300 es
    
    precision highp float;
    precision highp sampler3D;
    
    in vec3 pass_textureCoordinates;
    
    out vec4 color;
    
    uniform vec2 doseRange;

    uniform vec2 selectedRange;

    uniform float alphaLevelOpacity;

    uniform sampler3D sampler;
    
    vec3 hsv2rgb(float value){
        float a = 1.0-value;
        return vec3(1.0 - (a - 0.25) * 2.0, 2.0-4.0*abs(0.5 - a), (a - 0.5)*4.0);
    }

    void main(){
        float value = clamp(texture(sampler, pass_textureCoordinates).r, doseRange.x, doseRange.y);
        if(value < 0.1 || value < selectedRange.x || value > selectedRange.y){
            discard;
        }
        value /= doseRange.y;
        color = vec4(hsv2rgb(value), alphaLevelOpacity);
    }`;

    /**
     * The range of dose distribution values present in the {@link DoseTexture}.\
     * {@link range}.x -coordinate should be the smallest dose value present in the texture.\
     * {@link range}.y -coordinate should be the greatest dose value present in the texture.
     */
    public readonly range: UniformVec2;

    /**
     * The user selected range of dose distribution values present in the {@link DoseTexture}.\
     * {@link range}.x -coordinate should be the smallest dose value present in the texture.\
     * {@link range}.y -coordinate should be the greatest dose value present in the texture.
     */
    public readonly selectedRange: UniformVec2;

    /**
     * The opacity of the dose distribution.
     */
    public readonly alphaLevelOpacity: UniformFloat;

    /**
     * Method to construct a {@link DefaultDoseShader}.\
     * It's vertex stage has two input values:
     * * vec2 position;
     * * vec3 textureCoords;
     *
     * and fragment stage has a single {@link UniformVec2} {@link range}, that describes the texture's value range.
     */
    constructor() {
        super(
            DefaultDoseShader.VERTEX_CONTENT,
            DefaultDoseShader.FRAGMENT_CONTENT,
            'position',
            'textureCoords'
        );

        this.range = new UniformVec2('doseRange');

        this.selectedRange = new UniformVec2('selectedRange');

        this.alphaLevelOpacity = new UniformFloat('alphaLevelOpacity');

        this.storeUniformLocations(
            this.range,
            this.selectedRange,
            this.alphaLevelOpacity
        );
    }
}
